import { FC, useState } from 'react';
import Cookies from 'js-cookie';
import MainLogo from '../../../assets/Itv/Win_White 1.svg';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import { submitSurvey } from './surveyService';
import './SurveyPage.scss';

type Step = 'q1' | 'q2' | 'q3' | 'q4' | 'review' | 'submitting' | 'error' | 'thanks';

interface Answers {
  q1: string | null;
  q2: string[];
  q2Other: string;
  q3: string[];
  q3Other: string;
  q4: string[];
  q4Other: string;
}

const NO_OPTION_KEY = 'survey.q1.option-4';
const Q3_MAX = 3;
const Q4_MAX = 2;
const SURVEY_COOKIE = 'survey_one_submitted';

const Q1_OPTION_KEYS = ['survey.q1.option-1', 'survey.q1.option-2', 'survey.q1.option-3', 'survey.q1.option-4'];
const Q2_OPTION_KEYS = ['survey.q2.option-1', 'survey.q2.option-2', 'survey.q2.option-3', 'survey.q2.option-4'];
const Q3_OPTION_KEYS = [
  'survey.q3.option-1',
  'survey.q3.option-2',
  'survey.q3.option-3',
  'survey.q3.option-4',
  'survey.q3.option-5',
  'survey.q3.option-6',
  'survey.q3.option-7',
];
const Q4_OPTION_KEYS = [
  'survey.q4.option-1',
  'survey.q4.option-2',
  'survey.q4.option-3',
  'survey.q4.option-4',
  'survey.q4.option-5',
  'survey.q4.option-6',
];

const toggleInArray = (arr: string[], value: string, max: number): string[] => {
  if (arr.includes(value)) return arr.filter((v) => v !== value);

  if (arr.length < max) return [...arr, value];

  return arr;
};

const getProgress = (step: Step, isYes: boolean | null): number => {
  if (isYes === null) {
    const map: Record<string, number> = { q1: 25 };

    return map[step] || 0;
  }

  if (isYes) {
    const map: Record<string, number> = { q1: 25, q2: 50, q3: 75, review: 100, submitting: 100, error: 100 };

    return map[step] || 0;
  }

  const map: Record<string, number> = { q1: 33, q4: 66, review: 100, submitting: 100, error: 100 };

  return map[step] || 0;
};

export const SurveyPage: FC = () => {
  const { t } = useTranslations();
  const [step, setStep] = useState<Step>(Cookies.get(SURVEY_COOKIE) !== undefined ? 'thanks' : 'q1');
  const [answers, setAnswers] = useState<Answers>({
    q1: null,
    q2: [],
    q2Other: '',
    q3: [],
    q3Other: '',
    q4: [],
    q4Other: '',
  });

  const { q1, q2, q2Other, q3, q3Other, q4, q4Other } = answers;
  const noOptionValue = t(NO_OPTION_KEY);
  const isYes = step === 'q1' ? null : q1 !== null && q1 !== noOptionValue;

  const updateAnswer = <K extends keyof Answers>(key: K, value: Answers[K]): void => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const isYesPath = (val: string | null): boolean => val !== null && val !== noOptionValue;

  const goNext = (): void => {
    if (step === 'q1' && q1) setStep(isYesPath(q1) ? 'q2' : 'q4');
    else if (step === 'q2') setStep('q3');
    else if (step === 'q3' || step === 'q4') setStep('review');
  };

  const goBack = (): void => {
    if (step === 'q2' || step === 'q4') setStep('q1');
    else if (step === 'q3') setStep('q2');
    else if (step === 'review' || step === 'error') setStep(isYesPath(q1) ? 'q3' : 'q4');
  };

  const handleSubmit = async (): Promise<void> => {
    setStep('submitting');

    try {
      const res = await submitSurvey({
        q1,
        q2: q2.length > 0 ? q2 : undefined,
        q2Other: q2Other || undefined,
        q3: q3.length > 0 ? q3 : undefined,
        q3Other: q3Other || undefined,
        q4: q4.length > 0 ? q4 : undefined,
        q4Other: q4Other || undefined,
      });

      if (!res.ok) {
        setStep('error');

        return;
      }
    } catch {
      setStep('error');

      return;
    }

    setStep('thanks');
  };

  const showProgress = !['thanks', 'submitting', 'error'].includes(step);
  const isReviewLike = step === 'review' || step === 'submitting' || step === 'error';

  return (
    <div className="survey-page">
      <header className="survey-page__header">
        <img src={MainLogo} alt="ITV Win" className="survey-page__logo" />
      </header>
      <div className="survey-page__body">
        <h1 className="survey-page__title">{t('survey.title')}</h1>
        <p className="survey-page__description">{t('survey.description')}</p>

        {showProgress && (
          <div className="survey-progress">
            <div className="survey-progress__track">
              <div className="survey-progress__bar" style={{ width: `${getProgress(step, isYes)}%` }} />
            </div>
          </div>
        )}

        {step === 'q1' && (
          <div className="survey-question">
            <h2 className="survey-question__heading">{t('survey.q1.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.select-one')}</p>
            <div className="survey-question__options">
              {Q1_OPTION_KEYS.map((key) => (
                <label key={key} className={`survey-option ${q1 === t(key) ? 'survey-option--selected' : ''}`}>
                  <input
                    type="radio"
                    name="q1"
                    value={t(key)}
                    checked={q1 === t(key)}
                    onChange={(): void => updateAnswer('q1', t(key))}
                    className="survey-option__input"
                  />
                  <span className="survey-option__box" />
                  <span className="survey-option__label">{t(key)}</span>
                </label>
              ))}
            </div>
            <div className="survey-question__actions">
              <button type="button" className="survey-submit" disabled={!q1} onClick={goNext}>
                {t('survey.next')}
              </button>
            </div>
          </div>
        )}

        {step === 'q2' && (
          <div className="survey-question">
            <h2 className="survey-question__heading">{t('survey.q2.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.select-one')}</p>
            <div className="survey-question__options">
              {Q2_OPTION_KEYS.map((key) => (
                <label key={key} className={`survey-option ${q2.includes(t(key)) ? 'survey-option--selected' : ''}`}>
                  <input
                    type="checkbox"
                    name="q2"
                    value={t(key)}
                    checked={q2.includes(t(key))}
                    onChange={(): void => updateAnswer('q2', toggleInArray(q2, t(key), Infinity))}
                    className="survey-option__input"
                  />
                  <span className="survey-option__box" />
                  <span className="survey-option__label">{t(key)}</span>
                </label>
              ))}
            </div>
            <div className="survey-question__text-field">
              <label className="survey-question__text-label" htmlFor="q2-other">
                {t('survey.q2.other-label')}
              </label>
              <input
                id="q2-other"
                type="text"
                className="survey-question__text-input"
                value={q2Other}
                onChange={(e): void => updateAnswer('q2Other', e.target.value)}
              />
            </div>
            <div className="survey-question__actions">
              <button type="button" className="survey-back" onClick={goBack}>
                {t('survey.back')}
              </button>
              <button type="button" className="survey-submit" disabled={q2.length === 0} onClick={goNext}>
                {t('survey.next')}
              </button>
            </div>
          </div>
        )}

        {step === 'q3' && (
          <div className="survey-question">
            <h2 className="survey-question__heading">{t('survey.q3.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.select-up-to-3')}</p>
            <div className="survey-question__options">
              {Q3_OPTION_KEYS.map((key) => (
                <label key={key} className={`survey-option ${q3.includes(t(key)) ? 'survey-option--selected' : ''}`}>
                  <input
                    type="checkbox"
                    value={t(key)}
                    checked={q3.includes(t(key))}
                    onChange={(): void => updateAnswer('q3', toggleInArray(q3, t(key), Q3_MAX))}
                    className="survey-option__input"
                  />
                  <span className="survey-option__box" />
                  <span className="survey-option__label">{t(key)}</span>
                </label>
              ))}
            </div>
            <div className="survey-question__text-field">
              <label className="survey-question__text-label" htmlFor="q3-other">
                {t('survey.q3.other-label')}
              </label>
              <input
                id="q3-other"
                type="text"
                className="survey-question__text-input"
                value={q3Other}
                onChange={(e): void => updateAnswer('q3Other', e.target.value)}
              />
            </div>
            <div className="survey-question__actions">
              <button type="button" className="survey-back" onClick={goBack}>
                {t('survey.back')}
              </button>
              <button type="button" className="survey-submit" disabled={q3.length === 0} onClick={goNext}>
                {t('survey.review-answers')}
              </button>
            </div>
          </div>
        )}

        {step === 'q4' && (
          <div className="survey-question">
            <h2 className="survey-question__heading">{t('survey.q4.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.select-up-to-2')}</p>
            <div className="survey-question__options">
              {Q4_OPTION_KEYS.map((key) => (
                <label key={key} className={`survey-option ${q4.includes(t(key)) ? 'survey-option--selected' : ''}`}>
                  <input
                    type="checkbox"
                    value={t(key)}
                    checked={q4.includes(t(key))}
                    onChange={(): void => updateAnswer('q4', toggleInArray(q4, t(key), Q4_MAX))}
                    className="survey-option__input"
                  />
                  <span className="survey-option__box" />
                  <span className="survey-option__label">{t(key)}</span>
                </label>
              ))}
            </div>
            <div className="survey-question__text-field">
              <label className="survey-question__text-label" htmlFor="q4-other">
                {t('survey.q4.other-label')}
              </label>
              <input
                id="q4-other"
                type="text"
                className="survey-question__text-input"
                value={q4Other}
                onChange={(e): void => updateAnswer('q4Other', e.target.value)}
              />
            </div>
            <div className="survey-question__actions">
              <button type="button" className="survey-back" onClick={goBack}>
                {t('survey.back')}
              </button>
              <button type="button" className="survey-submit" disabled={q4.length === 0} onClick={goNext}>
                {t('survey.review-answers')}
              </button>
            </div>
          </div>
        )}

        {isReviewLike && (
          <div className="survey-question">
            <h2 className="survey-question__heading">{t('survey.review.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.review.subtext')}</p>

            <div className="survey-review">
              <div className="survey-review__item">
                <h3 className="survey-review__label">{t('survey.review.q1-label')}</h3>
                <button type="button" className="survey-review__answer" onClick={(): void => setStep('q1')}>
                  {q1 || ''}
                </button>
              </div>

              {isYes && (
                <>
                  <div className="survey-review__item">
                    <h3 className="survey-review__label">{t('survey.review.q2-label')}</h3>
                    <button type="button" className="survey-review__answer" onClick={(): void => setStep('q2')}>
                      {q2.join(', ')}
                      {q2Other ? ` (Other: ${q2Other})` : ''}
                    </button>
                  </div>

                  <div className="survey-review__item">
                    <h3 className="survey-review__label">{t('survey.review.q3-label')}</h3>
                    <button type="button" className="survey-review__answer" onClick={(): void => setStep('q3')}>
                      {q3.join(', ')}
                      {q3Other ? ` (Other: ${q3Other})` : ''}
                    </button>
                  </div>
                </>
              )}

              {!isYes && (
                <div className="survey-review__item">
                  <h3 className="survey-review__label">{t('survey.review.q4-label')}</h3>
                  <button type="button" className="survey-review__answer" onClick={(): void => setStep('q4')}>
                    {q4.join(', ')}
                    {q4Other ? ` (Other: ${q4Other})` : ''}
                  </button>
                </div>
              )}
            </div>

            {step === 'error' && (
              <div className="survey-error">
                <p className="survey-error__message">{t('survey.error')}</p>
                <div className="survey-error__actions">
                  <button type="button" className="survey-back" onClick={goBack}>
                    {t('survey.back')}
                  </button>
                  <button type="button" className="survey-submit" onClick={handleSubmit}>
                    {t('survey.retry')}
                  </button>
                </div>
              </div>
            )}
            {step !== 'error' && (
              <div className="survey-question__actions">
                <button type="button" className="survey-back" onClick={goBack}>
                  {t('survey.back')}
                </button>
                <button type="button" className="survey-submit" disabled={step === 'submitting'} onClick={handleSubmit}>
                  {step === 'submitting' ? t('survey.submitting') : t('survey.submit')}
                </button>
              </div>
            )}
          </div>
        )}

        {step === 'thanks' && (
          <div className="survey-question survey-question--thanks">
            <h2 className="survey-question__heading">{t('survey.thanks.heading')}</h2>
            <p className="survey-question__subtext">{t('survey.thanks.subtext')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
